import { useState, useEffect } from 'react';
import { ZOOM_ON_CLICK, INITIAL_ZOOM_LEVEL, MAP_OPTIONS } from '../constants';
import { initMap, createMarker, getMarkerIcon } from 'utils/officesMap';

export const useOfficesMap = (
  wrapper,
  offices,
  coordinates,
  setCoordinates,
  selectedOffice,
  setSelectedOffice,
) => {
  const [mapInstance, setMapInstance] = useState(null);
  const [markers, setMarkers] = useState([]);
  // creating map
  useEffect(() => {
    if (!wrapper) return;
    const map = initMap(wrapper, coordinates, MAP_OPTIONS, INITIAL_ZOOM_LEVEL);

    setMapInstance(map);
  }, [wrapper]);
  // creating markers
  useEffect(() => {
    if (offices) {
      setMarkers(offices.map(office => createMarker(office, setSelectedOffice, setCoordinates)));
    }
  }, [offices]);
  // putting markers on the map
  useEffect(() => {
    if (mapInstance && markers) {
      markers.forEach(marker => {
        if (marker) {
          marker.setMap(mapInstance);
        }
      });
    }
  }, [mapInstance, markers]);
  // change size of marker when it should be active
  useEffect(() => {
    if (mapInstance && markers) {
      markers.forEach(marker => {
        if (marker) {
          if (selectedOffice?.office.city === marker.city) {
            marker.setIcon(getMarkerIcon('large'));
          } else {
            marker.setIcon(getMarkerIcon());
          }
        }
      });
    }
  }, [mapInstance, markers, selectedOffice]);
  // moving to the selected office on the map
  useEffect(() => {
    if (!mapInstance && !coordinates) return;
    const { LatLng } = window.google.maps;
    if (mapInstance && selectedOffice) {
      if (markers.some(({ visible }) => !visible)) {
        const updatedMarkers = markers.map(marker => {
          if (marker.hideOnZoomOut) {
            marker.setVisible(true);
          }
          return marker;
        });

        setMarkers(updatedMarkers);
      }
      if (mapInstance.getZoom() < ZOOM_ON_CLICK) {
        mapInstance.setZoom(ZOOM_ON_CLICK);
      }
      mapInstance.panTo(
        new LatLng({
          lat: Number(coordinates[0]),
          lng: Number(coordinates[1]),
        }),
      );
    }
  }, [coordinates, mapInstance]);

  useEffect(() => {
    if (!mapInstance) return;
    // hide markers which doesn't showing on zoom-out
    let updatedMarkers = [...markers];
    const zoomEvent = window.google.maps.event.addListener(
      mapInstance,
      'zoom_changed',
      function () {
        if (mapInstance.getZoom() < INITIAL_ZOOM_LEVEL) {
          setSelectedOffice(null);
          updatedMarkers = markers.map(marker => {
            if (marker.hideOnZoomOut) {
              marker.setVisible(false);
            }
            return marker;
          });
        } else {
          updatedMarkers = markers.map(marker => {
            if (marker.hideOnZoomOut) {
              marker.setVisible(true);
            }
            return marker;
          });
        }
      },
    );
    setMarkers(updatedMarkers);
    // remove office popup when click on map area
    const clickEvent = window.google.maps.event.addListener(mapInstance, 'click', function () {
      setSelectedOffice(null);
    });
    // remove office popup when move the map
    const moveEvent = window.google.maps.event.addListener(
      mapInstance,
      'center_changed',
      function () {
        if (Number(coordinates[0]).toFixed(3) - mapInstance.getCenter().lat().toFixed(3) <= 0.1) {
          setSelectedOffice(null);
        }
      },
    );

    return () => {
      window.google.maps.event.removeListener(zoomEvent);
      window.google.maps.event.removeListener(clickEvent);
      window.google.maps.event.removeListener(moveEvent);
    };
  }, [mapInstance, coordinates]);

  return { mapInstance };
};
